import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../useAuth';

interface AvailableClass {
  id: string;
  title: string;
  provider: string;
  credits: number;
  date: string;
  categories: string[];
  deliveryMethod: 'Online' | 'In-Person' | 'Hybrid';
  link: string;
  description: string;
  states: string[];
}

const AdminClassBrowser: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [classes, setClasses] = useState<AvailableClass[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');

  useEffect(() => {
    // Redirect if not admin
    if (user?.role !== 'admin') {
      navigate('/');
      return;
    }

    // Simulate fetching classes (in real implementation, this would call an API)
    const fetchClasses = async () => {
      setLoading(true);
      
      // Mock data - simulating what we'd scrape from AIA, NCARB, etc.
      const mockClasses: AvailableClass[] = [
        {
          id: '1',
          title: 'Sustainable Design Strategies for Commercial Buildings',
          provider: 'AIA Continuing Education',
          credits: 1.5,
          date: '2025-12-15',
          categories: ['HSW', 'Sustainable Design'],
          deliveryMethod: 'Online',
          link: 'https://www.aia.org/courses/example1',
          description: 'Explore advanced sustainable design strategies for commercial projects including energy modeling and material selection.',
          states: ['CA', 'NY', 'TX', 'FL']
        },
        {
          id: '2',
          title: 'Building Code Updates 2024: Life Safety Systems',
          provider: 'NCARB',
          credits: 2.0,
          date: '2025-11-20',
          categories: ['HSW', 'Building Codes'],
          deliveryMethod: 'Online',
          link: 'https://www.ncarb.org/courses/example2',
          description: 'Comprehensive review of 2024 IBC updates focusing on life safety, egress, and fire protection systems.',
          states: ['All States']
        },
        {
          id: '3',
          title: 'Accessible Design: Beyond ADA Compliance',
          provider: 'University of Washington',
          credits: 1.0,
          date: '2025-12-01',
          categories: ['HSW', 'Accessibility'],
          deliveryMethod: 'Online',
          link: 'https://www.uwex.edu/courses/example3',
          description: 'Advanced accessibility design strategies that go beyond minimum ADA requirements for inclusive environments.',
          states: ['CA', 'WA', 'OR']
        },
        {
          id: '4',
          title: 'Structural Systems for High-Rise Design',
          provider: 'Thornton Tomasetti',
          credits: 1.5,
          date: '2025-11-28',
          categories: ['Structural Engineering', 'Design'],
          deliveryMethod: 'Hybrid',
          link: 'https://www.thorntontomasetti.com/courses/example4',
          description: 'Examination of lateral and gravity systems for tall buildings with case studies from recent projects.',
          states: ['NY', 'CA', 'IL']
        },
        {
          id: '5',
          title: 'Ethics in Architecture: Professional Responsibility',
          provider: 'AIA Ethics Board',
          credits: 1.0,
          date: '2025-12-10',
          categories: ['Ethics', 'Professional Practice'],
          deliveryMethod: 'Online',
          link: 'https://www.aia.org/ethics/example5',
          description: 'Required ethics training covering professional responsibility, client relationships, and code of conduct.',
          states: ['All States']
        },
        {
          id: '6',
          title: 'Mass Timber Construction: Design and Detailing',
          provider: 'WoodWorks',
          credits: 2.0,
          date: '2025-12-05',
          categories: ['HSW', 'Building Materials'],
          deliveryMethod: 'Online',
          link: 'https://www.woodworks.org/courses/example6',
          description: 'Introduction to mass timber design including CLT, glulam, and connections for commercial applications.',
          states: ['CA', 'OR', 'WA', 'CO']
        },
        {
          id: '7',
          title: 'Historic Preservation: Rehabilitation Standards',
          provider: 'National Park Service',
          credits: 1.5,
          date: '2025-11-25',
          categories: ['Historic Preservation', 'Design'],
          deliveryMethod: 'Online',
          link: 'https://www.nps.gov/courses/example7',
          description: 'Secretary of Interior Standards for historic rehabilitation with case studies and practical applications.',
          states: ['All States']
        },
        {
          id: '8',
          title: 'Advanced Revit: BIM for Large Projects',
          provider: 'Autodesk University',
          credits: 1.0,
          date: '2025-12-12',
          categories: ['Technology', 'BIM'],
          deliveryMethod: 'Online',
          link: 'https://www.autodesk.com/au/example8',
          description: 'Advanced BIM workflows for large-scale projects including worksharing, family creation, and collaboration.',
          states: ['CA', 'NY', 'TX']
        },
        {
          id: '9',
          title: 'Climate Resilient Design: Flood and Storm Mitigation',
          provider: 'FEMA',
          credits: 2.0,
          date: '2025-12-08',
          categories: ['HSW', 'Sustainable Design'],
          deliveryMethod: 'Online',
          link: 'https://www.fema.gov/courses/example9',
          description: 'Design strategies for climate resilience focusing on flood mitigation, storm resistance, and adaptation.',
          states: ['FL', 'LA', 'TX', 'NC', 'SC']
        },
        {
          id: '10',
          title: 'Lighting Design Fundamentals for Architects',
          provider: 'Illuminating Engineering Society',
          credits: 1.5,
          date: '2025-11-30',
          categories: ['Design', 'MEP Systems'],
          deliveryMethod: 'Hybrid',
          link: 'https://www.ies.org/courses/example10',
          description: 'Fundamentals of architectural lighting design including daylighting, artificial lighting, and controls.',
          states: ['CA', 'NY', 'IL']
        }
      ];

      // Simulate network delay
      await new Promise(resolve => setTimeout(resolve, 500));
      
      setClasses(mockClasses);
      setLoading(false);
    };

    fetchClasses();
  }, [user, navigate]);

  // Get unique categories
  const categories = ['All', ...Array.from(new Set(classes.flatMap(c => c.categories)))];

  // Filter classes
  const filteredClasses = classes.filter(cls => {
    const matchesSearch = cls.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         cls.provider.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         cls.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || cls.categories.includes(selectedCategory);
    return matchesSearch && matchesCategory;
  });

  const handleAddClass = (cls: AvailableClass) => {
    // In future implementation, this would navigate to Add Class page with pre-filled data
    const confirmed = window.confirm(
      `Add this class to your library?\n\n` +
      `Title: ${cls.title}\n` +
      `Provider: ${cls.provider}\n` +
      `Credits: ${cls.credits} CEC\n` +
      `Date: ${new Date(cls.date).toLocaleDateString()}\n\n` +
      `This would auto-populate the "Add New Class" form.`
    );
    
    if (confirmed) {
      alert('Future implementation: This will redirect to Add Class page with pre-filled data from this class.');
    }
  };

  if (loading) {
    return (
      <div className="container mt-4">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1">Available CEC Classes for Architects</h2>
          <p className="text-muted mb-0">
            <i className="bi bi-info-circle me-1"></i>
            Admin Test Page - Proof of Concept for Class Discovery Feature
          </p>
        </div>
        <button className="btn btn-outline-primary" onClick={() => navigate('/admin')}>
          <i className="bi bi-arrow-left me-2"></i>Back to Admin
        </button>
      </div>

      {/* Search and Filter Controls */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-8">
              <label className="form-label fw-bold">
                <i className="bi bi-search me-2"></i>Search Classes
              </label>
              <input
                type="text"
                className="form-control"
                placeholder="Search by title, provider, or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label fw-bold">
                <i className="bi bi-funnel me-2"></i>Filter by Category
              </label>
              <select
                className="form-select"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Results Summary */}
      <div className="alert alert-info mb-3">
        <i className="bi bi-info-circle me-2"></i>
        Showing <strong>{filteredClasses.length}</strong> of <strong>{classes.length}</strong> available classes
      </div>

      {/* Class List */}
      <div className="row g-3">
        {filteredClasses.map(cls => (
          <div key={cls.id} className="col-12">
            <div className="card h-100 border-primary">
              <div className="card-body">
                <div className="row">
                  <div className="col-md-8">
                    <h5 className="card-title mb-2">
                      {cls.title}
                      <span className="badge bg-primary ms-2">{cls.credits} CEC</span>
                    </h5>
                    <p className="text-muted mb-2">
                      <i className="bi bi-building me-1"></i>
                      <strong>{cls.provider}</strong>
                      <span className="mx-2">|</span>
                      <i className="bi bi-calendar me-1"></i>
                      {new Date(cls.date).toLocaleDateString()}
                      <span className="mx-2">|</span>
                      <i className={`bi ${cls.deliveryMethod === 'Online' ? 'bi-laptop' : cls.deliveryMethod === 'In-Person' ? 'bi-people' : 'bi-diagram-3'} me-1`}></i>
                      {cls.deliveryMethod}
                    </p>
                    <p className="card-text mb-2">{cls.description}</p>
                    <div className="mb-2">
                      <small className="text-muted d-block mb-1">
                        <strong>Categories:</strong>
                      </small>
                      {cls.categories.map(cat => (
                        <span key={cat} className="badge bg-secondary me-1">{cat}</span>
                      ))}
                    </div>
                    <div>
                      <small className="text-muted">
                        <strong>Accepted in:</strong> {cls.states.join(', ')}
                      </small>
                    </div>
                  </div>
                  <div className="col-md-4 d-flex flex-column justify-content-between align-items-end">
                    <div className="w-100 mb-2">
                      <a
                        href={cls.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-sm btn-outline-primary w-100 mb-2"
                      >
                        <i className="bi bi-box-arrow-up-right me-2"></i>
                        View Details
                      </a>
                      <button
                        className="btn btn-sm btn-success w-100"
                        onClick={() => handleAddClass(cls)}
                      >
                        <i className="bi bi-plus-circle me-2"></i>
                        Add to My Classes
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredClasses.length === 0 && (
        <div className="alert alert-warning text-center">
          <i className="bi bi-exclamation-triangle me-2"></i>
          No classes found matching your search criteria. Try adjusting your filters.
        </div>
      )}

      {/* Future Implementation Notes */}
      <div className="card mt-4 bg-light">
        <div className="card-body">
          <h6 className="card-title">
            <i className="bi bi-lightbulb me-2"></i>
            Future Implementation Notes
          </h6>
          <ul className="mb-0">
            <li>Real-time web scraping from AIA, NCARB, state boards, and universities</li>
            <li>API integration with major CEC providers</li>
            <li>Database caching with weekly/monthly refresh</li>
            <li>Advanced filters: location, price, delivery method, date range</li>
            <li>One-click add: automatically populate Add Class form with all details</li>
            <li>User bookmarks/favorites for classes of interest</li>
            <li>Email notifications for new classes in saved categories</li>
            <li>Integration with cycle requirements to show relevant classes</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AdminClassBrowser;
